import { ReactNode } from "react";
import { MDXProvider } from "@mdx-js/react";
import { VideoEmbed } from "./VideoEmbed";
import { AudioPlayer } from "./AudioPlayer";
import { Callout } from "./Callout";
import { ImageGallery, Gallery } from "./ImageGallery";
import { generateSlug } from "./TableOfContents";

// Helper to extract text from children (handles nested elements)
function getTextFromChildren(children: ReactNode): string {
  if (typeof children === "string") return children;
  if (typeof children === "number") return String(children);
  if (Array.isArray(children)) {
    return children.map(getTextFromChildren).join("");
  }
  if (children && typeof children === "object" && "props" in children) {
    return getTextFromChildren((children as any).props.children);
  }
  return "";
}

// Custom components available in all MDX files
const components = {
  VideoEmbed,
  AudioPlayer,
  Callout,
  ImageGallery,
  Gallery,
  // Override default HTML elements for consistent styling
  h1: ({ children }: { children: ReactNode }) => (
    <h1 className="font-system text-3xl md:text-4xl font-bold tracking-tight mt-12 mb-5 text-[#000000]">
      {children}
    </h1>
  ),
  h2: ({ children }: { children: ReactNode }) => {
    const text = getTextFromChildren(children);
    const id = generateSlug(text);
    return (
      <h2
        id={id}
        className="font-system text-2xl md:text-3xl font-bold tracking-tight mt-12 mb-4 text-[#000000] scroll-mt-24"
      >
        {children}
      </h2>
    );
  },
  h3: ({ children }: { children: ReactNode }) => {
    const text = getTextFromChildren(children);
    const id = generateSlug(text);
    return (
      <h3
        id={id}
        className="font-system text-xl md:text-2xl font-semibold tracking-tight mt-10 mb-3 text-[#000000] scroll-mt-24"
      >
        {children}
      </h3>
    );
  },
  p: ({ children }: { children: ReactNode }) => (
    <p className="font-system my-6 leading-[1.7] text-[#000000] text-lg">{children}</p>
  ),
  a: ({ href, children }: { href?: string; children: ReactNode }) => (
    <a
      href={href}
      className="font-system text-[#000000] underline underline-offset-2 hover:text-[#000000]/70 transition-colors"
      target={href?.startsWith("http") ? "_blank" : undefined}
      rel={href?.startsWith("http") ? "noopener noreferrer" : undefined}
    >
      {children}
    </a>
  ),
  ul: ({ children }: { children: ReactNode }) => (
    <ul className="font-system my-6 pl-6 list-disc space-y-2 text-[#000000] text-lg">{children}</ul>
  ),
  ol: ({ children }: { children: ReactNode }) => (
    <ol className="font-system my-6 pl-6 list-decimal space-y-2 text-[#000000] text-lg">{children}</ol>
  ),
  li: ({ children }: { children: ReactNode }) => (
    <li className="font-system leading-[1.7] text-lg">{children}</li>
  ),
  blockquote: ({ children }: { children: ReactNode }) => (
    <blockquote className="font-system my-8 pl-5 border-l-[3px] border-[#000000]/30 italic text-[#000000]/80 text-lg leading-[1.7]">
      {children}
    </blockquote>
  ),
  code: ({ children }: { children: ReactNode }) => (
    <code className="px-1.5 py-0.5 bg-[#000000]/10 rounded text-[0.95em] font-mono">
      {children}
    </code>
  ),
  pre: ({ children }: { children: ReactNode }) => (
    <pre className="my-6 p-4 bg-[#000000] text-[#FFFFFF] rounded-lg overflow-x-auto">
      {children}
    </pre>
  ),
  hr: () => <hr className="my-12 border-t border-[#000000]/15" />,
  img: ({ src, alt }: { src?: string; alt?: string }) => (
    <figure className="my-10">
      <img
        src={src}
        alt={alt}
        className="w-full h-auto rounded-lg"
        loading="lazy"
      />
      {alt && (
        <figcaption className="font-system mt-3 text-center text-sm text-[#000000]/55 italic">
          {alt}
        </figcaption>
      )}
    </figure>
  ),
};

interface MDXContentProps {
  children: ReactNode;
}

export function MDXContent({ children }: MDXContentProps) {
  return (
    <MDXProvider components={components}>
      <article className="font-system prose prose-lg max-w-none text-[#000000] prose-headings:font-system prose-p:font-system prose-li:font-system prose-blockquote:font-system prose-img:mx-auto prose-img:w-full prose-img:rounded-lg">
        {children}
      </article>
    </MDXProvider>
  );
}
